import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  ScrollText,
  ShieldAlert,
  Users,
  Menu,
  LogOut,
  ChevronDown,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSentinel } from "@/context/SentinelContext";
import { Switch } from "@/components/ui/switch";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useState } from "react";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard-link" },
  { to: "/incidents", label: "Incidents", icon: ScrollText, testid: "nav-incidents-link" },
  { to: "/emergency", label: "Emergency", icon: ShieldAlert, testid: "nav-emergency-link" },
  { to: "/contacts", label: "Contacts", icon: Users, testid: "nav-contacts-link" },
];

function Brand() {
  return (
    <Link to="/dashboard" className="flex items-center gap-2.5" data-testid="brand-logo">
      <span className="flex h-7 w-7 items-center justify-center rounded border border-sentinel-border bg-sentinel-accent/10">
        <ShieldAlert className="h-4 w-4 text-sentinel-accent" strokeWidth={2.2} />
      </span>
      <span className="font-heading text-[15px] font-bold tracking-[0.2em] text-slate-100">
        SENTINEL
      </span>
    </Link>
  );
}

function NavItems({ onNavigate }) {
  return (
    <nav className="flex flex-col gap-1">
      {NAV.map(({ to, label, icon: Icon, testid }) => (
        <NavLink
          key={to}
          to={to}
          data-testid={testid}
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              "group flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
              isActive
                ? "bg-sentinel-accent/12 text-slate-100"
                : "text-slate-400 hover:bg-[#1C2030] hover:text-slate-100"
            )
          }
        >
          {({ isActive }) => (
            <>
              <Icon
                className={cn(
                  "h-4 w-4",
                  isActive ? "text-sentinel-accent" : "text-slate-500 group-hover:text-slate-300"
                )}
                strokeWidth={2}
              />
              {label}
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

function GuardianToggle() {
  const { guardianMode, setGuardianMode } = useSentinel();
  return (
    <div className="flex items-center gap-2.5 rounded-md border border-sentinel-border bg-sentinel-deep px-3 py-1.5">
      <span
        className={cn(
          "h-1.5 w-1.5 rounded-full",
          guardianMode ? "bg-emerald-400" : "bg-slate-600"
        )}
      />
      <span className="hidden font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400 sm:inline">
        Guardian
      </span>
      <Switch
        checked={guardianMode}
        onCheckedChange={setGuardianMode}
        data-testid="guardian-mode-toggle"
        aria-label="Toggle Guardian Mode"
      />
      <span
        className={cn(
          "font-mono text-[10px] font-semibold uppercase tracking-widest",
          guardianMode ? "text-emerald-400" : "text-slate-500"
        )}
        data-testid="guardian-mode-state"
      >
        {guardianMode ? "ON" : "OFF"}
      </span>
    </div>
  );
}

function AccountMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const initials = (user?.name || "OP").slice(0, 2).toUpperCase();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="flex items-center gap-2 rounded-md border border-sentinel-border bg-sentinel-deep px-2 py-1.5 text-slate-200 transition-colors hover:border-sentinel-borderhover"
          data-testid="account-menu-trigger"
        >
          <span className="flex h-6 w-6 items-center justify-center rounded bg-sentinel-accent/15 font-mono text-[11px] font-semibold text-sentinel-accent">
            {initials}
          </span>
          <ChevronDown className="h-3.5 w-3.5 text-slate-500" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 border-sentinel-border bg-sentinel-card">
        <DropdownMenuLabel className="font-normal">
          <p className="text-sm font-medium text-slate-100">{user?.name}</p>
          <p className="font-mono text-xs text-slate-500">{user?.email}</p>
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-sentinel-border" />
        <DropdownMenuItem
          className="cursor-pointer text-slate-300 focus:bg-[#1C2030] focus:text-slate-100"
          onClick={() => {
            logout();
            navigate("/");
          }}
          data-testid="logout-button"
        >
          <LogOut className="mr-2 h-4 w-4" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function DashboardLayout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="min-h-screen bg-sentinel-bg">
      <header className="sticky top-0 z-40 border-b border-sentinel-border bg-sentinel-bg/85 backdrop-blur-md">
        <div className="flex h-14 items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button
                  className="rounded-md border border-sentinel-border p-1.5 text-slate-300 lg:hidden"
                  data-testid="mobile-nav-trigger"
                  aria-label="Open navigation"
                >
                  <Menu className="h-4 w-4" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64 border-sentinel-border bg-sentinel-deep p-4">
                <div className="mb-6 mt-1">
                  <Brand />
                </div>
                <NavItems onNavigate={() => setMobileOpen(false)} />
              </SheetContent>
            </Sheet>
            <Brand />
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <GuardianToggle />
            <AccountMenu />
          </div>
        </div>
      </header>

      <div className="mx-auto flex max-w-[1400px]">
        <aside className="sticky top-14 hidden h-[calc(100vh-3.5rem)] w-60 shrink-0 border-r border-sentinel-border px-4 py-6 lg:block">
          <NavItems />
          <div className="mt-6 rounded-md border border-sentinel-border bg-sentinel-deep p-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
              System
            </p>
            <p className="mt-1.5 flex items-center gap-2 text-sm text-slate-300">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
              All sensors nominal
            </p>
          </div>
        </aside>

        <main className="relative min-w-0 flex-1 px-4 py-6 sm:px-6 lg:px-8">
          <div className="pointer-events-none absolute inset-x-0 top-0 h-72 spotlight-cyan" />
          <div className="relative">{children}</div>
        </main>
      </div>
    </div>
  );
}
